import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Heading,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import { TenantId } from '@pelicargo/tenants';
import { Show } from '@pelicargo/ui';

import { SimpleInput } from '../SimpleInput';
import { SimpleSelect } from '../SimpleSelect';
import { SimpleTextarea } from '../SimpleTextarea';
import { TEXT_TRANSFORM_OPTIONS, ThemeField, ThemeFieldGroup, ThemeSection, ThemeSectionName } from './sections';
import { ThemeAssetInput } from './ThemeAssetInput';
import { ThemeColorInput } from './ThemeColorInput';

const ThemeFieldControl = ({ field, tenantId }: { field: ThemeField; tenantId?: TenantId }) => {
  // Checked ahead of the switch rather than as a case, so a slotless asset field still renders the
  // plain input below instead of nothing. `ASSET_FIELDS` always sets `slot`; this is the narrowing.
  if (field.kind === 'asset' && field.slot) {
    return <ThemeAssetInput name={field.name} label={field.label} slot={field.slot} tenantId={tenantId} />;
  }

  switch (field.kind) {
    case 'color':
      return <ThemeColorInput name={field.name} label={field.label} helper={field.helper} />;
    case 'transform':
      return <SimpleSelect name={field.name} label={field.label} options={TEXT_TRANSFORM_OPTIONS} />;
    case 'copy':
      // The default string is the placeholder, so staff can read what they are replacing. The
      // schema caps a copy value at 2000 characters; enforcing it here keeps the failure inside
      // the control instead of arriving as a submit that silently does nothing.
      return (
        <SimpleTextarea
          name={field.name}
          label={field.label}
          placeholder={field.placeholder}
          rows={2}
          maxLength={2000}
        />
      );
    default:
      return (
        <SimpleInput name={field.name} label={field.label} placeholder={field.placeholder} helper={field.helper} />
      );
  }
};

const ThemeFieldGroupRow = ({
  group,
  columns,
  tenantId,
}: {
  group: ThemeFieldGroup;
  columns: number;
  tenantId?: TenantId;
}) => {
  const showTitle = !!group.title;

  return (
    <Stack spacing="2">
      <Show if={showTitle}>
        <Heading variant="h6" fontSize="xs" textTransform="uppercase" opacity="0.6">
          {group.title}
        </Heading>
      </Show>
      <SimpleGrid columns={{ base: 1, md: 2, xl: columns }} spacingX="4" spacingY="2">
        {group.fields.map((field) => (
          <ThemeFieldControl key={field.name} field={field} tenantId={tenantId} />
        ))}
      </SimpleGrid>
    </Stack>
  );
};

type Props = {
  section: ThemeSection;
  isDisabled?: boolean;
  /** Which tenant is being edited. Only the asset controls need it - that is where uploads go. */
  tenantId?: TenantId;
  onResetSection: (section: ThemeSectionName) => void;
};

const COLUMNS_BY_SECTION: Partial<Record<ThemeSectionName, number>> = {
  // Copy is long prose and gets the full width; an asset control carries a preview and two
  // buttons, so four across is unreadable. Everything else is short CSS values.
  copy: 1,
  assets: 2,
};

export const ThemeSectionPanel = ({ section, isDisabled, tenantId, onResetSection }: Props) => {
  const columns = COLUMNS_BY_SECTION[section.name] ?? 4;

  const handleResetSection = () => onResetSection(section.name);

  return (
    <AccordionItem>
      <AccordionButton>
        <Stack spacing="0" flex="1" textAlign="left" py="1">
          <Heading variant="h6">{section.title}</Heading>
          <Text fontSize="xs" opacity="0.7">
            {section.description}
          </Text>
        </Stack>
        <AccordionIcon />
      </AccordionButton>
      <AccordionPanel>
        <Stack spacing="4">
          <Box>
            <Button size="xs" variant="outline" colorScheme="red" isDisabled={isDisabled} onClick={handleResetSection}>
              Reset section
            </Button>
          </Box>
          {section.groups.map((group, index) => (
            <ThemeFieldGroupRow
              key={group.title ?? String(index)}
              group={group}
              columns={columns}
              tenantId={tenantId}
            />
          ))}
        </Stack>
      </AccordionPanel>
    </AccordionItem>
  );
};
